import type { ExpressionSpecification } from 'mapbox-gl';
import type { TrailResult } from '../redux/reducer';

const tileTrailCountExpression: ExpressionSpecification = ['coalesce', ['get', 'trail_count'], 0];
const noTrailheadsExpression: ExpressionSpecification = ['literal', false];

export type TrailheadListState = {
  isTrailCompleted?: (trailId: number) => boolean;
  isTrailSaved?: (trailId: number) => boolean;
};

export type TrailheadResultExpressions = {
  /** True for the trailheads where every trail is closed. */
  isClosedExpression: ExpressionSpecification;
  /** True for the trailheads where every trail is completed. */
  isCompletedExpression: ExpressionSpecification;
  /** True for the trailheads where at least one trail is saved. */
  isSavedExpression: ExpressionSpecification;
  /** Draws only the trailheads with at least one trail in the search results. */
  matchedTrailheadsFilter: ExpressionSpecification;
  popularityExpression: ExpressionSpecification | number;
  trailCountExpression: ExpressionSpecification;
};

const trailheadIdsExpression = (trailheadIds: number[]): ExpressionSpecification =>
  trailheadIds.length ? ['in', ['id'], ['literal', trailheadIds]] : noTrailheadsExpression;

/**
 * Reconciles the trailhead tiles with the trails explore currently has from the explore service, so
 * filtering the search updates what the pins say and which ones are drawn at all. Mapbox can't read
 * the search results or iterate a tile's `trail_ids`, so every value is resolved here and matched per
 * trailhead id.
 *
 * The saved and completed states live in the user's lists rather than on the tile, which is why
 * they come in as callbacks and leave as id lists the pin layers can test with `in`.
 *
 * `matchedTrailheadsFilter` names the trailheads to draw rather than the ones to hide, so a tile
 * that loaded since the last pass through `trailIdsByTrailheadId` waits for that pass instead of
 * flashing a pin the search results would have dropped.
 */
export function buildTrailheadResultExpressions(
  trailResults: Record<string, TrailResult[]> | undefined,
  trailIdsByTrailheadId: Record<string, number[]>,
  { isTrailCompleted, isTrailSaved }: TrailheadListState = {}
): TrailheadResultExpressions {
  const hasTrailResults = Object.values(trailResults ?? {}).some(trails => trails.length);
  if (!hasTrailResults) {
    return {
      isClosedExpression: noTrailheadsExpression,
      isCompletedExpression: noTrailheadsExpression,
      isSavedExpression: noTrailheadsExpression,
      matchedTrailheadsFilter: noTrailheadsExpression,
      popularityExpression: 0,
      trailCountExpression: tileTrailCountExpression
    };
  }

  const popularityExpression: ExpressionSpecification = ['match', ['id']];
  const trailCountExpression: ExpressionSpecification = ['match', ['id']];
  const matchedTrailheadIds: number[] = [];
  const closedTrailheadIds: number[] = [];
  const completedTrailheadIds: number[] = [];
  const savedTrailheadIds: number[] = [];

  Object.entries(trailIdsByTrailheadId).forEach(([trailheadId, trailIds]) => {
    const id = Number(trailheadId);
    if (!Number.isFinite(id)) {
      return;
    }

    const trails = trailResultsForIds(trailResults, trailIds);
    if (!trails.length) {
      return;
    }
    matchedTrailheadIds.push(id);
    trailCountExpression.push(id, trails.length);

    if (trails.every(trail => trail.is_closed)) {
      closedTrailheadIds.push(id);
    }
    // A trailhead stands for several trails, so one saved trail is enough to badge it, the same way
    // the grouped trail pins treat a partially saved group.
    if (isTrailSaved && trails.some(trail => isTrailSaved(Number(trail.ID)))) {
      savedTrailheadIds.push(id);
    }
    if (isTrailCompleted && trails.every(trail => isTrailCompleted(Number(trail.ID)))) {
      completedTrailheadIds.push(id);
    }

    const popularities = trails.map(trail => trail.popularity).filter((popularity): popularity is number => typeof popularity === 'number');
    if (popularities.length) {
      popularityExpression.push(id, popularities.reduce((total, popularity) => total + popularity, 0) / popularities.length);
    }
  });

  const hasPopularity = popularityExpression.length > 2;
  const hasTrailCounts = trailCountExpression.length > 2;
  popularityExpression.push(0);
  trailCountExpression.push(tileTrailCountExpression);

  return {
    isClosedExpression: trailheadIdsExpression(closedTrailheadIds),
    isCompletedExpression: trailheadIdsExpression(completedTrailheadIds),
    isSavedExpression: trailheadIdsExpression(savedTrailheadIds),
    matchedTrailheadsFilter: trailheadIdsExpression(matchedTrailheadIds),
    popularityExpression: hasPopularity ? popularityExpression : 0,
    trailCountExpression: hasTrailCounts ? trailCountExpression : tileTrailCountExpression
  };
}

export function parseTrailheadTrailIds(trailIds: unknown): number[] {
  if (typeof trailIds !== 'string' || !trailIds.startsWith('{') || !trailIds.endsWith('}')) {
    return [];
  }

  return trailIds
    .slice(1, -1)
    .split(',')
    .map(trailId => Number(trailId.trim()))
    .filter(trailId => Number.isSafeInteger(trailId) && trailId > 0);
}

export function trailResultsForIds(trailResults: Record<string, TrailResult[]> | undefined, trailIds: number[]): TrailResult[] {
  if (!trailResults || !trailIds.length) {
    return [];
  }

  const trailsById = new Map<number, TrailResult>();
  Object.values(trailResults).forEach(trails => {
    trails.forEach(trail => {
      if (!trailsById.has(trail.ID)) {
        trailsById.set(trail.ID, trail);
      }
    });
  });

  return trailIds.flatMap(trailId => {
    const trail = trailsById.get(trailId);
    if (!trail) {
      return [];
    }

    return [trail];
  });
}
